import { OperatorType } from 'angular-slickgrid';
import { BaseRangeFilter, ModeOption } from './base-range-filter';
import { localizeWithFallback } from '../../utils/localization-utils';
import { DEFAULT_TIME_DATE } from '../../constants/numeric-constants';

export class CustomHourRangeFilter extends BaseRangeFilter<string> {
  private equalInputElm!: HTMLInputElement;
  private fromInputElm!: HTMLInputElement;
  private toInputElm!: HTMLInputElement;
  private defaultTimeDate = DEFAULT_TIME_DATE;

  override init(args: any): void {
    if (args?.columnDef?.filter?.params) {
      this.defaultTimeDate =
        args.columnDef.filter.params.defaultTimeDate ?? DEFAULT_TIME_DATE;
    }

    super.init(args);
  }

  protected getModeOptions(): ModeOption[] {
    return [
      {
        id: 'equal',
        label: localizeWithFallback(this.localizationService, 'LanguageService::EqualTo'),
        default: true,
      },
      {
        id: 'range',
        label: localizeWithFallback(this.localizationService, 'LanguageService::Between'),
      },
    ];
  }

  override clear(shouldTriggerQuery = true): void {
    this.equalInputElm && (this.equalInputElm.value = '');
    this.fromInputElm && (this.fromInputElm.value = '');
    this.toInputElm && (this.toInputElm.value = '');
    super.clear(shouldTriggerQuery);
  }

  protected createInputsForMode(mode: string): HTMLElement {
    const container = document.createElement('div');
    const inputType = 'time';

    if (mode === 'equal') {
      this.equalInputElm = document.createElement('input');
      this.equalInputElm.type = inputType;
      this.equalInputElm.className = 'form-control';

      this.equalInputElm.addEventListener('input', () =>
        this.updateApplyButtonState()
      );
      this.equalInputElm.addEventListener('keydown', e => {
        if (e.key === 'Enter') this.applyButtonElm.click();
      });

      container.appendChild(this.equalInputElm);
    }

    if (mode === 'range') {
      container.className = 'between-inputs';

      this.fromInputElm = document.createElement('input');
      this.fromInputElm.type = inputType;
      this.fromInputElm.className = 'form-control';

      this.toInputElm = document.createElement('input');
      this.toInputElm.type = inputType;
      this.toInputElm.className = 'form-control';

      [this.fromInputElm, this.toInputElm].forEach(input => {
        input.addEventListener('input', () =>
          this.updateApplyButtonState()
        );
        input.addEventListener('keydown', e => {
          if (e.key === 'Enter') this.applyButtonElm.click();
        });
      });

      container.appendChild(this.fromInputElm);
      container.appendChild(this.toInputElm);
    }

    return container;
  }

  protected areInputsAvailable(): boolean {
    return (
      (this.currentMode === 'equal' && !!this.equalInputElm) ||
      (this.currentMode === 'range' && !!this.fromInputElm && !!this.toInputElm)
    );
  }

  protected getValuesFromInputs(): any[] {
    if (!this.areInputsAvailable()) {
      return this.searchTerms;
    }

    if (this.currentMode === 'equal') {
      const datetime = this.toDateTime(this.equalInputElm?.value);
      return datetime ? [datetime] : [];
    }

    if (this.currentMode === 'range') {
      const from = this.toDateTime(this.fromInputElm?.value);
      const to = this.toDateTime(this.toInputElm?.value);

      if (!from && !to) return [];

      return [from, to];
    }

    return [];
  }

  protected setValuesInInputs(values: any[]): void {
    if (!values || values.length === 0) return;

    const targetMode = values.length === 1 ? 'equal' : 'range';

    if (this.currentMode !== targetMode) {
      this.currentMode = targetMode;
      this.updateModeSelection();
      this.updateInputsForMode();
    }

    setTimeout(() => {
      if (targetMode === 'equal' && this.equalInputElm) {
        this.equalInputElm.value = this.extractTime(values[0]);
      }

      if (targetMode === 'range') {
        this.fromInputElm &&
          (this.fromInputElm.value = values[0]
            ? this.extractTime(values[0])
            : '');
        this.toInputElm &&
          (this.toInputElm.value = values[1]
            ? this.extractTime(values[1])
            : '');
      }
    });
  }

  private extractTime(value: string): string {
    if (!value) return '';

    if (/^\d{2}:\d{2}:\d{2}$/.test(value)) {
      return value.substring(0, 5);
    }

    if (/^\d{2}:\d{2}$/.test(value)) {
      return value;
    }

    if (value.includes('T')) {
      return value.split('T')[1]?.substring(0, 5) ?? '';
    }

    return '';
  }

  private toDateTime(timeValue?: string | null): string | null {
    const time = timeValue?.trim();
    if (!time) {
      return null;
    }

    const normalizedTime = time.length === 5 ? `${time}:00` : time;
    return `${this.defaultTimeDate}T${normalizedTime}`;
  }

  protected updateApplyButtonState(): void {
    let isValid = false;

    if (this.currentMode === 'equal') {
      isValid = !!this.equalInputElm?.value;
    }

    if (this.currentMode === 'range') {
      const from = this.fromInputElm?.value;
      const to = this.toInputElm?.value;

      if (from || to) {
        isValid = !from || !to || to >= from;
      }
    }

    this.applyButtonElm.disabled = !isValid;
    this.errorElm && (this.errorElm.textContent = '');
  }

  protected onModeChanged(): void {
    this.updateApplyButtonState();
  }

  protected formatDisplayValue(values: any[]): string {
    if (!values || values.length === 0) {
      this.toggleHeaderFilledClass(false);
      return '';
    }

    let result = '';

    if (this.currentMode === 'equal') {
      result = `= ${this.extractTime(values[0])}`;
    }

    if (this.currentMode === 'range') {
      const [from, to] = values;
      if (from && to) result = `${this.extractTime(from)} - ${this.extractTime(to)}`;
      else if (from) result = `>= ${this.extractTime(from)}`;
      else if (to) result = `<= ${this.extractTime(to)}`;
    }

    this.toggleHeaderFilledClass(true);
    return result;
  }

  protected getOperatorForMode(mode: string): OperatorType {
    return mode === 'equal'
      ? OperatorType.equal
      : OperatorType.rangeInclusive;
  }

  private toggleHeaderFilledClass(isFilled: boolean): void {
    isFilled
      ? this.headerInputElm.classList.add('filled')
      : this.headerInputElm.classList.remove('filled');
  }
}
